import { useState, useEffect } from 'react'
import axios from 'axios'
import toast from 'react-hot-toast'
import { isFormStylingApplied, isButtonStylingApplied } from '../../utils/sectionFormStyling'

export default function PublicFixturesSection({ website, section, sectionFormStyling, useDefaultStyling = true, sectionType = 'fixtures' }) {
  const [fixtures, setFixtures] = useState([])
  const [loading, setLoading] = useState(true)
  const [filteredFixtures, setFilteredFixtures] = useState([])
  const [filterStatus, setFilterStatus] = useState('all')
  const isFormStyling = useDefaultStyling || isFormStylingApplied(sectionFormStyling, sectionType, section?._id)
  const isButtonStyling = useDefaultStyling || isButtonStylingApplied(sectionFormStyling, sectionType, section?._id)
  
  // Default values to fallback to if styling is not provided
  const DEFAULT_FORM_COLORS = {
    backgroundColor: '#FFFFFF',
    textColor: '#111827',
    hoverBackgroundColor: '#F9FAFB',
    hoverTextColor: '#374151',
    buttonBackgroundColor: '#3B82F6',
    buttonTextColor: '#FFFFFF',
    hoverButtonBackgroundColor: '#2563eb',
    hoverButtonTextColor: '#ffffff'
  }
  
  // Ensure we have valid styling values
  const formColors = isFormStyling ? (sectionFormStyling || DEFAULT_FORM_COLORS) : undefined
  
  const formStyle = isFormStyling ? {
    backgroundColor: formColors?.backgroundColor || DEFAULT_FORM_COLORS.backgroundColor,
    color: formColors?.textColor || DEFAULT_FORM_COLORS.textColor
  } : undefined
  const buttonStyle = isButtonStyling ? {
    backgroundColor: formColors?.buttonBackgroundColor || DEFAULT_FORM_COLORS.buttonBackgroundColor,
    color: formColors?.buttonTextColor || DEFAULT_FORM_COLORS.buttonTextColor,
    borderColor: formColors?.buttonBackgroundColor || DEFAULT_FORM_COLORS.buttonBackgroundColor
  } : undefined
  const textStyle = isFormStyling ? { color: formColors?.textColor || DEFAULT_FORM_COLORS.textColor } : undefined
  const filterButtonStyle = isButtonStyling ? buttonStyle : textStyle
  const [topScorers, setTopScorers] = useState([])
  const [standings, setStandings] = useState([])

  useEffect(() => {
    fetchFixtures()
    fetchTopScorers()
    fetchStandings()
  }, [website])

  useEffect(() => {
    if (filterStatus === 'all') {
      setFilteredFixtures(fixtures)
    } else {
      setFilteredFixtures(fixtures.filter(f => f.status === filterStatus))
    }
  }, [fixtures, filterStatus])

  const fetchFixtures = async () => {
    try {
      setLoading(true)
      const response = await axios.get(`/fixtures/website/${website._id}`)
      const sorted = (response.data.fixtures || []).sort((a, b) => 
        new Date(b.matchDate) - new Date(a.matchDate)
      )
      setFixtures(sorted)
    } catch (error) {
      console.error('[PublicFixtures] Error loading:', error)
      toast.error('Failed to load fixtures')
    } finally {
      setLoading(false)
    }
  }

  const fetchTopScorers = async () => {
    try {
      const res = await axios.get(`/fixtures/player-stats/${website._id}`)
      setTopScorers(res.data.players || [])
    } catch (err) {
      console.error('[PublicFixtures] Error loading top scorers:', err)
    }
  }

  const fetchStandings = async () => {
    try {
      const res = await axios.get(`/fixtures/standings/${website._id}`)
      setStandings(res.data.standings || [])
    } catch (err) {
      console.error('[PublicFixtures] Error loading standings:', err)
    }
  }

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      weekday: 'short',
      month: 'short',
      day: 'numeric',
      year: 'numeric'
    })
  }

  /**
   * Convert UTC time to user's local timezone and format as 12-hour
   */
  const convertUTCToLocalTime = (utcDate, fixtureTimezone) => {
    try {
      const userTimezone = Intl.DateTimeFormat().resolvedOptions().timeZone
      
      const formatter = new Intl.DateTimeFormat('en-US', {
        timeZone: userTimezone,
        hour: '2-digit',
        minute: '2-digit',
        hour12: false
      })
      
      const parts = formatter.formatToParts(new Date(utcDate))
      const hour = parseInt(parts.find(p => p.type === 'hour')?.value || '00')
      const minute = parts.find(p => p.type === 'minute')?.value || '00'
      
      const period = hour >= 12 ? 'PM' : 'AM'
      const hour12 = hour % 12 || 12
      
      return `${String(hour12).padStart(2, '0')}:${minute} ${period}`
    } catch (error) {
      console.error('Error converting time:', error)
      // Fallback to storage timezone time
      const formatter = new Intl.DateTimeFormat('en-US', {
        timeZone: fixtureTimezone || 'UTC',
        hour: '2-digit',
        minute: '2-digit',
        hour12: true
      })
      return formatter.format(new Date(utcDate))
    }
  }

  const getStatusColor = (status) => {
    switch (status) {
      case 'finished':
        return 'bg-green-100 text-green-800'
      case 'live':
        return 'bg-red-100 text-red-800'
      default:
        return 'bg-yellow-100 text-yellow-800'
    }
  }

  if (loading) {
    return (
      <div className="text-center py-8">
        <div className="inline-block animate-spin rounded-full h-8 w-8 border-b-2 border-primary-600"></div>
      </div>
    )
  }

  return (
    <div className="space-y-6" style={formStyle}>
      {/* Filter Tabs */}
      <div className="flex gap-2 flex-wrap border-b border-gray-200 pb-4">
        <button
          onClick={() => setFilterStatus('all')}
          style={filterButtonStyle}
          className={`px-4 py-2 font-medium transition-colors ${
            filterStatus === 'all'
              ? 'border-b-2 border-primary-600'
              : 'hover:opacity-80'
          }`}
        >
          All Fixtures ({fixtures.length})
        </button>
        <button
          onClick={() => setFilterStatus('upcoming')}
          style={filterButtonStyle}
          className={`px-4 py-2 font-medium transition-colors ${
            filterStatus === 'upcoming'
              ? 'border-b-2 border-primary-600'
              : 'hover:opacity-80'
          }`}
        >
          Upcoming ({fixtures.filter(f => f.status === 'upcoming').length})
        </button>
        <button
          onClick={() => setFilterStatus('live')}
          style={filterButtonStyle}
          className={`px-4 py-2 font-medium transition-colors ${
            filterStatus === 'live'
              ? 'border-b-2 border-primary-600'
              : 'hover:opacity-80'
          }`}
        >
          Live ({fixtures.filter(f => f.status === 'live').length})
        </button>
        <button
          onClick={() => setFilterStatus('finished')}
          style={filterButtonStyle}
          className={`px-4 py-2 font-medium transition-colors ${
            filterStatus === 'finished'
              ? 'border-b-2 border-primary-600'
              : 'hover:opacity-80'
          }`}
        >
          Finished ({fixtures.filter(f => f.status === 'finished').length})
        </button>
      </div>

      {/* Fixtures List */}
      {/* Top Scorers */}
      {topScorers && topScorers.length > 0 && (
        <div className="border border-gray-200 rounded-lg p-4" style={isFormStyling ? { backgroundColor: sectionFormStyling.backgroundColor, color: sectionFormStyling.textColor } : undefined}>
          <h3 className="text-lg font-semibold mb-2" style={textStyle}>⚽ Top Scorers</h3>
          <ol className="list-decimal list-inside space-y-2">
            {topScorers
              .filter(pl => pl.goals && pl.goals > 0)
              .sort((a, b) => (b.goals || 0) - (a.goals || 0))
              .slice(0, 10)
              .map((pl, idx) => (
                <li key={`${pl.teamName}-${pl.playerName}`} className="text-sm" style={textStyle}>
                  <span className="font-medium">{pl.playerName}</span> — <span className="font-bold text-primary-600">{pl.goals}</span> goals
                  <span className="text-xs ml-2" style={textStyle}>({pl.teamName})</span>
                </li>
              ))}
          </ol>
        </div>
      )}

      {/* League Table / Standings */}
      {standings && standings.length > 0 && (
        <div className="border border-gray-200 rounded-lg overflow-hidden" style={isFormStyling ? { backgroundColor: sectionFormStyling.backgroundColor, color: sectionFormStyling.textColor } : undefined}>
          <div className="p-4 border-b border-gray-200 bg-gradient-to-r from-gray-50 to-white">
            <h3 className="text-lg font-semibold" style={textStyle}>🏆 League Standings</h3>
          </div>
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="bg-gradient-to-r from-gray-100 to-gray-50 border-b-2 border-gray-300">
                <tr>
                  <th className="px-4 py-3 text-left text-sm font-bold" style={textStyle}>Pos</th>
                  <th className="px-4 py-3 text-left text-sm font-bold" style={textStyle}>Team</th>
                  <th className="px-4 py-3 text-center text-sm font-bold" style={textStyle}>P</th>
                  <th className="px-4 py-3 text-center text-sm font-bold" style={textStyle}>W</th>
                  <th className="px-4 py-3 text-center text-sm font-bold" style={textStyle}>D</th>
                  <th className="px-4 py-3 text-center text-sm font-bold" style={textStyle}>L</th>
                  <th className="px-4 py-3 text-center text-sm font-bold" style={textStyle}>GF</th>
                  <th className="px-4 py-3 text-center text-sm font-bold" style={textStyle}>GA</th>
                  <th className="px-4 py-3 text-center text-sm font-bold" style={textStyle}>GD</th>
                  <th className="px-4 py-3 text-center text-sm font-bold text-white bg-primary-600">Pts</th>
                </tr>
              </thead>
              <tbody>
                {standings.map((standing, idx) => (
                  <tr
                    key={standing._id}
                    className={`border-b border-gray-200 hover:bg-gray-50 transition-colors ${
                      idx % 2 === 0 ? 'bg-white' : 'bg-gray-50'
                    }`}
                  >
                    <td className="px-4 py-3">
                      <div className="flex items-center justify-center">
                        <span
                          className={`w-8 h-8 flex items-center justify-center rounded-full font-bold text-sm ${
                            idx === 0
                              ? 'bg-yellow-400 text-yellow-900'
                              : idx === 1
                              ? 'bg-gray-400 text-white'
                              : idx === 2
                              ? 'bg-orange-400 text-white'
                              : 'bg-gray-200 text-gray-700'
                          }`}
                        >
                          {standing.position}
                        </span>
                      </div>
                    </td>
                    <td className="px-4 py-3">
                      <p className="font-semibold" style={textStyle}>{standing.teamName}</p>
                    </td>
                    <td className="px-4 py-3 text-center text-sm font-medium" style={textStyle}>{standing.played}</td>
                    <td className="px-4 py-3 text-center text-sm font-medium text-green-700 bg-green-50">
                      {standing.wins}
                    </td>
                    <td className="px-4 py-3 text-center text-sm font-medium text-blue-700 bg-blue-50">
                      {standing.draws}
                    </td>
                    <td className="px-4 py-3 text-center text-sm font-medium text-red-700 bg-red-50">
                      {standing.losses}
                    </td>
                    <td className="px-4 py-3 text-center text-sm font-semibold" style={textStyle}>{standing.goalsFor}</td>
                    <td className="px-4 py-3 text-center text-sm font-semibold" style={textStyle}>
                      {standing.goalsAgainst}
                    </td>
                    <td className="px-4 py-3 text-center text-sm font-semibold" style={textStyle}>
                      <span
                        className={`inline-block px-2 py-1 rounded ${
                          standing.goalDifference > 0
                            ? 'bg-green-100 text-green-800'
                            : standing.goalDifference < 0
                            ? 'bg-red-100 text-red-800'
                            : 'bg-gray-100 text-gray-800'
                        }`}
                      >
                        {standing.goalDifference > 0 ? '+' : ''}
                        {standing.goalDifference}
                      </span>
                    </td>
                    <td className="px-4 py-3 text-center text-sm font-bold text-white bg-primary-600">
                      {standing.points}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {filteredFixtures.length === 0 ? (
        <div className="text-center py-12 bg-gray-50 rounded-lg">
          <p style={textStyle}>No {filterStatus !== 'all' ? filterStatus : ''} fixtures to display</p>
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {filteredFixtures.map((fixture) => (
            <div
              key={fixture._id}
              className="border border-gray-200 rounded-lg p-6 hover:shadow-lg transition-shadow"
              style={isFormStyling ? {
                backgroundColor: sectionFormStyling.backgroundColor,
                color: sectionFormStyling.textColor
              } : undefined}
            >
              {/* Status Badge */}
              <div className="mb-3">
                <span className={`inline-block px-3 py-1 text-xs font-semibold rounded-full ${getStatusColor(fixture.status)}`}>
                  {fixture.status === 'finished' ? '✓ Finished' :
                   fixture.status === 'live' ? '● Live' :
                   '📅 Upcoming'}
                </span>
              </div>

              {/* Match */}
              <div className="mb-4">
                <div className="flex items-center justify-between">
                  <div className="flex-1 text-right">
                    <p className="font-semibold" style={textStyle}>{fixture.homeTeam}</p>
                    <p className="text-xs" style={textStyle}>Home</p>
                  </div>
                  <div className="mx-4 px-3 py-2 bg-gray-100 rounded">
                    <p className="font-bold text-lg" style={textStyle}>
                      {['finished', 'live'].includes(fixture.status) && (fixture.homeScore !== undefined && fixture.awayScore !== undefined) ? (
                        `${fixture.homeScore} - ${fixture.awayScore}`
                      ) : (
                        'VS'
                      )}
                    </p>
                    {/* Live Time Display */}
                    {fixture.status === 'live' && fixture.liveMatchTime?.mode && (
                      <p className="text-xs font-medium text-red-600 mt-1">
                        {fixture.liveMatchTime.currentMinute}' - {fixture.liveMatchTime.matchPhase}
                        {fixture.liveMatchTime.stoppageTime > 0 && ` +${fixture.liveMatchTime.stoppageTime}`}
                      </p>
                    )}
                  </div>
                  <div className="flex-1 text-left">
                    <p className="font-semibold" style={textStyle}>{fixture.awayTeam}</p>
                    <p className="text-xs" style={textStyle}>Away</p>
                  </div>
                </div>
              </div>

              {/* Details */}
              <div className="space-y-2 text-sm border-t pt-4" style={textStyle}>
                <div className="flex items-start gap-2">
                  <span className="font-medium min-w-fit" style={textStyle}>📅 Date:</span>
                  <span>{formatDate(fixture.matchDate)}</span>
                </div>
                <div className="flex items-start gap-2">
                  <span className="font-medium min-w-fit" style={textStyle}>⏰ Time:</span>
                  <span className="font-medium">
                    {fixture.matchTimeUTC 
                      ? convertUTCToLocalTime(fixture.matchTimeUTC, fixture.timezone)
                      : fixture.matchTime}
                  </span>
                  {fixture.timezone && fixture.timezone !== 'UTC' && (
                    <span className="text-xs ml-1" style={textStyle}>
                      ({fixture.timezone})
                    </span>
                  )}
                </div>
                {fixture.venue && (
                  <div className="flex items-start gap-2">
                    <span className="font-medium min-w-fit" style={textStyle}>🏟️ Venue:</span>
                    <span>{fixture.venue}</span>
                  </div>
                )}
                {fixture.competition && (
                  <div className="flex items-start gap-2">
                    <span className="font-medium min-w-fit" style={textStyle}>🏆 League:</span>
                    <span>{fixture.competition}</span>
                  </div>
                )}
              </div>

              {/* Goal Scorers - for Live/Finished Matches */}
              {['finished', 'live'].includes(fixture.status) && fixture.goalScorers && (
                <div className="border-t pt-4 mt-4">
                  <h4 className="font-semibold text-sm mb-3" style={textStyle}>⚽ Goal Scorers</h4>
                  <div className="grid grid-cols-2 gap-4">
                    {/* Home Team Scorers */}
                    <div>
                      <p className="text-xs font-medium mb-2" style={textStyle}>{fixture.homeTeam}</p>
                      {fixture.goalScorers.homeTeam && fixture.goalScorers.homeTeam.length > 0 ? (
                        <ul className="space-y-1">
                          {fixture.goalScorers.homeTeam.map((scorer, idx) => (
                            <li key={idx} className="text-xs" style={textStyle}>
                              <span className="font-medium">{scorer.playerName}</span>
                              <span className="ml-1" style={textStyle}>({scorer.goalsCount})</span>
                            </li>
                          ))}
                        </ul>
                      ) : (
                        <p className="text-xs italic" style={textStyle}>No goal scorers recorded</p>
                      )}
                    </div>

                    {/* Away Team Scorers */}
                    <div>
                      <p className="text-xs font-medium mb-2" style={textStyle}>{fixture.awayTeam}</p>
                      {fixture.goalScorers.awayTeam && fixture.goalScorers.awayTeam.length > 0 ? (
                        <ul className="space-y-1">
                          {fixture.goalScorers.awayTeam.map((scorer, idx) => (
                            <li key={idx} className="text-xs" style={textStyle}>
                              <span className="font-medium">{scorer.playerName}</span>
                              <span className="ml-1" style={textStyle}>({scorer.goalsCount})</span>
                            </li>
                          ))}
                        </ul>
                      ) : (
                        <p className="text-xs italic" style={textStyle}>No goal scorers recorded</p>
                      )}
                    </div>
                  </div>
                </div>
              )}

              {/* Cards - Live/Finished */}
              {['finished', 'live'].includes(fixture.status) && (fixture.yellowCards || fixture.redCards) && (
                <div className="border-t pt-4 mt-4">
                  <h4 className="font-semibold text-sm mb-3" style={textStyle}>🟨 / 🟥 Cards</h4>
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <p className="text-xs font-medium mb-2" style={textStyle}>{fixture.homeTeam}</p>
                      {fixture.yellowCards?.homeTeam?.length > 0 && (
                        <div className="mb-2">
                          <p className="text-xs font-medium" style={textStyle}>Yellow</p>
                          <ul className="space-y-1">
                            {fixture.yellowCards.homeTeam.map((c, idx) => (
                              <li key={idx} className="text-xs" style={textStyle}><span className="font-medium">{c.playerName}</span> <span className="ml-1" style={textStyle}>({c.cardsCount})</span></li>
                            ))}
                          </ul>
                        </div>
                      )}
                      {fixture.redCards?.homeTeam?.length > 0 && (
                        <div>
                          <p className="text-xs font-medium" style={textStyle}>Red</p>
                          <ul className="space-y-1">
                            {fixture.redCards.homeTeam.map((c, idx) => (
                              <li key={idx} className="text-xs" style={textStyle}><span className="font-medium">{c.playerName}</span> <span className="ml-1" style={textStyle}>({c.cardsCount})</span></li>
                            ))}
                          </ul>
                        </div>
                      )}
                    </div>

                    <div>
                      <p className="text-xs font-medium mb-2" style={textStyle}>{fixture.awayTeam}</p>
                      {fixture.yellowCards?.awayTeam?.length > 0 && (
                        <div className="mb-2">
                          <p className="text-xs font-medium" style={textStyle}>Yellow</p>
                          <ul className="space-y-1">
                            {fixture.yellowCards.awayTeam.map((c, idx) => (
                              <li key={idx} className="text-xs" style={textStyle}><span className="font-medium">{c.playerName}</span> <span className="ml-1" style={textStyle}>({c.cardsCount})</span></li>
                            ))}
                          </ul>
                        </div>
                      )}
                      {fixture.redCards?.awayTeam?.length > 0 && (
                        <div>
                          <p className="text-xs font-medium" style={textStyle}>Red</p>
                          <ul className="space-y-1">
                            {fixture.redCards.awayTeam.map((c, idx) => (
                              <li key={idx} className="text-xs" style={textStyle}><span className="font-medium">{c.playerName}</span> <span className="ml-1" style={textStyle}>({c.cardsCount})</span></li>
                            ))}
                          </ul>
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  )
}














// import { useState, useEffect } from 'react'
// import axios from 'axios'
// import toast from 'react-hot-toast'

// export default function PublicFixturesSection({ website, section }) {
//   const [fixtures, setFixtures] = useState([])
//   const [loading, setLoading] = useState(true)
//   const [filteredFixtures, setFilteredFixtures] = useState([])
//   const [filterStatus, setFilterStatus] = useState('all')
//   const [topScorers, setTopScorers] = useState([])

//   useEffect(() => {
//     fetchFixtures()
//     fetchTopScorers()
//   }, [website])

//   useEffect(() => {
//     if (filterStatus === 'all') {
//       setFilteredFixtures(fixtures)
//     } else {
//       setFilteredFixtures(fixtures.filter(f => f.status === filterStatus))
//     }
//   }, [fixtures, filterStatus])

//   const fetchFixtures = async () => {
//     try {
//       setLoading(true)
//       const response = await axios.get(`/fixtures/website/${website._id}`)
//       const sorted = (response.data.fixtures || []).sort((a, b) => 
//         new Date(b.matchDate) - new Date(a.matchDate)
//       )
//       setFixtures(sorted)
//     } catch (error) {
//       console.error('[PublicFixtures] Error loading:', error)
//       toast.error('Failed to load fixtures')
//     } finally {
//       setLoading(false)
//     }
//   }

//   const fetchTopScorers = async () => {
//     try {
//       const res = await axios.get(`/fixtures/player-stats/${website._id}`)
//       setTopScorers(res.data.players || [])
//     } catch (err) {
//       console.error('[PublicFixtures] Error loading top scorers:', err)
//     }
//   }

//   const formatDate = (dateString) => {
//     return new Date(dateString).toLocaleDateString('en-US', {
//       weekday: 'short',
//       month: 'short',
//       day: 'numeric',
//       year: 'numeric'
//     })
//   }

//   /**
//    * Convert UTC time to user's local timezone and format as 12-hour
//    */
//   const convertUTCToLocalTime = (utcDate, fixtureTimezone) => {
//     try {
//       const userTimezone = Intl.DateTimeFormat().resolvedOptions().timeZone
      
//       const formatter = new Intl.DateTimeFormat('en-US', {
//         timeZone: userTimezone,
//         hour: '2-digit',
//         minute: '2-digit',
//         hour12: false
//       })
      
//       const parts = formatter.formatToParts(new Date(utcDate))
//       const hour = parseInt(parts.find(p => p.type === 'hour')?.value || '00')
//       const minute = parts.find(p => p.type === 'minute')?.value || '00'
      
//       const period = hour >= 12 ? 'PM' : 'AM'
//       const hour12 = hour % 12 || 12
      
//       return `${String(hour12).padStart(2, '0')}:${minute} ${period}`
//     } catch (error) {
//       console.error('Error converting time:', error)
//       // Fallback to storage timezone time
//       const formatter = new Intl.DateTimeFormat('en-US', {
//         timeZone: fixtureTimezone || 'UTC',
//         hour: '2-digit',
//         minute: '2-digit',
//         hour12: true
//       })
//       return formatter.format(new Date(utcDate))
//     }
//   }

//   const getStatusColor = (status) => {
//     switch (status) {
//       case 'finished':
//         return 'bg-green-100 text-green-800'
//       case 'live':
//         return 'bg-red-100 text-red-800'
//       default:
//         return 'bg-yellow-100 text-yellow-800'
//     }
//   }

//   if (loading) {
//     return (
//       <div className="text-center py-8">
//         <div className="inline-block animate-spin rounded-full h-8 w-8 border-b-2 border-primary-600"></div>
//       </div>
//     )
//   }

//   return (
//     <div className="space-y-6">
//       {/* Filter Tabs */}
//       <div className="flex gap-2 flex-wrap border-b border-gray-200 pb-4">
//         <button
//           onClick={() => setFilterStatus('all')}
//           className={`px-4 py-2 font-medium transition-colors ${
//             filterStatus === 'all'
//               ? 'text-primary-600 border-b-2 border-primary-600'
//               : 'text-gray-600 hover:text-gray-900'
//           }`}
//         >
//           All Fixtures ({fixtures.length})
//         </button>
//         <button
//           onClick={() => setFilterStatus('upcoming')}
//           className={`px-4 py-2 font-medium transition-colors ${
//             filterStatus === 'upcoming'
//               ? 'text-primary-600 border-b-2 border-primary-600'
//               : 'text-gray-600 hover:text-gray-900'
//           }`}
//         >
//           Upcoming ({fixtures.filter(f => f.status === 'upcoming').length})
//         </button>
//         <button
//           onClick={() => setFilterStatus('live')}
//           className={`px-4 py-2 font-medium transition-colors ${
//             filterStatus === 'live'
//               ? 'text-primary-600 border-b-2 border-primary-600'
//               : 'text-gray-600 hover:text-gray-900'
//           }`}
//         >
//           Live ({fixtures.filter(f => f.status === 'live').length})
//         </button>
//         <button
//           onClick={() => setFilterStatus('finished')}
//           className={`px-4 py-2 font-medium transition-colors ${
//             filterStatus === 'finished'
//               ? 'text-primary-600 border-b-2 border-primary-600'
//               : 'text-gray-600 hover:text-gray-900'
//           }`}
//         >
//           Finished ({fixtures.filter(f => f.status === 'finished').length})
//         </button>
//       </div>

//       {/* Fixtures List */}
//       {/* Top Scorers */}
//       {topScorers && topScorers.length > 0 && (
//         <div className="bg-white border border-gray-200 rounded-lg p-4">
//           <h3 className="text-lg font-semibold text-gray-900 mb-2">Top Scorers</h3>
//           <ol className="list-decimal list-inside">
//             {topScorers.slice(0, 10).map((pl, idx) => (
//               <li key={`${pl.teamName}-${pl.playerName}`} className="text-sm text-gray-700">
//                 <span className="font-medium">{pl.playerName}</span> — {pl.goals} goals
//                 <span className="text-xs text-gray-500 ml-2">({pl.teamName})</span>
//               </li>
//             ))}
//           </ol>
//         </div>
//       )}
//       {filteredFixtures.length === 0 ? (
//         <div className="text-center py-12 bg-gray-50 rounded-lg">
//           <p className="text-gray-500">No {filterStatus !== 'all' ? filterStatus : ''} fixtures to display</p>
//         </div>
//       ) : (
//         <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
//           {filteredFixtures.map((fixture) => (
//             <div key={fixture._id} className="bg-white border border-gray-200 rounded-lg p-6 hover:shadow-lg transition-shadow">
//               {/* Status Badge */}
//               <div className="mb-3">
//                 <span className={`inline-block px-3 py-1 text-xs font-semibold rounded-full ${getStatusColor(fixture.status)}`}>
//                   {fixture.status === 'finished' ? '✓ Finished' :
//                    fixture.status === 'live' ? '● Live' :
//                    '📅 Upcoming'}
//                 </span>
//               </div>

//               {/* Match */}
//               <div className="mb-4">
//                 <div className="flex items-center justify-between">
//                   <div className="flex-1 text-right">
//                     <p className="font-semibold text-gray-900">{fixture.homeTeam}</p>
//                     <p className="text-xs text-gray-500">Home</p>
//                   </div>
//                   <div className="mx-4 px-3 py-2 bg-gray-100 rounded">
//                     <p className="font-bold text-lg text-gray-900">
//                       {['finished', 'live'].includes(fixture.status) && (fixture.homeScore !== undefined && fixture.awayScore !== undefined) ? (
//                         `${fixture.homeScore} - ${fixture.awayScore}`
//                       ) : (
//                         'VS'
//                       )}
//                     </p>
//                     {/* Live Time Display */}
//                     {fixture.status === 'live' && fixture.liveMatchTime?.mode && (
//                       <p className="text-xs font-medium text-red-600 mt-1">
//                         {fixture.liveMatchTime.currentMinute}' - {fixture.liveMatchTime.matchPhase}
//                         {fixture.liveMatchTime.stoppageTime > 0 && ` +${fixture.liveMatchTime.stoppageTime}`}
//                       </p>
//                     )}
//                   </div>
//                   <div className="flex-1 text-left">
//                     <p className="font-semibold text-gray-900">{fixture.awayTeam}</p>
//                     <p className="text-xs text-gray-500">Away</p>
//                   </div>
//                 </div>
//               </div>

//               {/* Details */}
//               <div className="space-y-2 text-sm text-gray-600 border-t pt-4">
//                 <div className="flex items-start gap-2">
//                   <span className="font-medium text-gray-700 min-w-fit">📅 Date:</span>
//                   <span>{formatDate(fixture.matchDate)}</span>
//                 </div>
//                 <div className="flex items-start gap-2">
//                   <span className="font-medium text-gray-700 min-w-fit">⏰ Time:</span>
//                   <span className="font-medium">
//                     {fixture.matchTimeUTC 
//                       ? convertUTCToLocalTime(fixture.matchTimeUTC, fixture.timezone)
//                       : fixture.matchTime}
//                   </span>
//                   {fixture.timezone && fixture.timezone !== 'UTC' && (
//                     <span className="text-xs text-gray-500 ml-1">
//                       ({fixture.timezone})
//                     </span>
//                   )}
//                 </div>
//                 {fixture.venue && (
//                   <div className="flex items-start gap-2">
//                     <span className="font-medium text-gray-700 min-w-fit">🏟️ Venue:</span>
//                     <span>{fixture.venue}</span>
//                   </div>
//                 )}
//                 {fixture.competition && (
//                   <div className="flex items-start gap-2">
//                     <span className="font-medium text-gray-700 min-w-fit">🏆 League:</span>
//                     <span>{fixture.competition}</span>
//                   </div>
//                 )}
//               </div>

//               {/* Goal Scorers - for Live/Finished Matches */}
//               {['finished', 'live'].includes(fixture.status) && fixture.goalScorers && (
//                 <div className="border-t pt-4 mt-4">
//                   <h4 className="font-semibold text-gray-900 text-sm mb-3">⚽ Goal Scorers</h4>
//                   <div className="grid grid-cols-2 gap-4">
//                     {/* Home Team Scorers */}
//                     <div>
//                       <p className="text-xs font-medium text-gray-600 mb-2">{fixture.homeTeam}</p>
//                       {fixture.goalScorers.homeTeam && fixture.goalScorers.homeTeam.length > 0 ? (
//                         <ul className="space-y-1">
//                           {fixture.goalScorers.homeTeam.map((scorer, idx) => (
//                             <li key={idx} className="text-xs text-gray-700">
//                               <span className="font-medium">{scorer.playerName}</span>
//                               <span className="text-gray-600 ml-1">({scorer.goalsCount})</span>
//                             </li>
//                           ))}
//                         </ul>
//                       ) : (
//                         <p className="text-xs text-gray-500 italic">No goal scorers recorded</p>
//                       )}
//                     </div>

//                     {/* Away Team Scorers */}
//                     <div>
//                       <p className="text-xs font-medium text-gray-600 mb-2">{fixture.awayTeam}</p>
//                       {fixture.goalScorers.awayTeam && fixture.goalScorers.awayTeam.length > 0 ? (
//                         <ul className="space-y-1">
//                           {fixture.goalScorers.awayTeam.map((scorer, idx) => (
//                             <li key={idx} className="text-xs text-gray-700">
//                               <span className="font-medium">{scorer.playerName}</span>
//                               <span className="text-gray-600 ml-1">({scorer.goalsCount})</span>
//                             </li>
//                           ))}
//                         </ul>
//                       ) : (
//                         <p className="text-xs text-gray-500 italic">No goal scorers recorded</p>
//                       )}
//                     </div>
//                   </div>
//                 </div>
//               )}

//               {/* Cards - Live/Finished */}
//               {['finished', 'live'].includes(fixture.status) && (fixture.yellowCards || fixture.redCards) && (
//                 <div className="border-t pt-4 mt-4">
//                   <h4 className="font-semibold text-gray-900 text-sm mb-3">🟨 / 🟥 Cards</h4>
//                   <div className="grid grid-cols-2 gap-4">
//                     <div>
//                       <p className="text-xs font-medium text-gray-600 mb-2">{fixture.homeTeam}</p>
//                       {fixture.yellowCards?.homeTeam?.length > 0 && (
//                         <div className="mb-2">
//                           <p className="text-xs text-gray-700 font-medium">Yellow</p>
//                           <ul className="space-y-1">
//                             {fixture.yellowCards.homeTeam.map((c, idx) => (
//                               <li key={idx} className="text-xs text-gray-700"><span className="font-medium">{c.playerName}</span> <span className="text-gray-600 ml-1">({c.cardsCount})</span></li>
//                             ))}
//                           </ul>
//                         </div>
//                       )}
//                       {fixture.redCards?.homeTeam?.length > 0 && (
//                         <div>
//                           <p className="text-xs text-gray-700 font-medium">Red</p>
//                           <ul className="space-y-1">
//                             {fixture.redCards.homeTeam.map((c, idx) => (
//                               <li key={idx} className="text-xs text-gray-700"><span className="font-medium">{c.playerName}</span> <span className="text-gray-600 ml-1">({c.cardsCount})</span></li>
//                             ))}
//                           </ul>
//                         </div>
//                       )}
//                     </div>

//                     <div>
//                       <p className="text-xs font-medium text-gray-600 mb-2">{fixture.awayTeam}</p>
//                       {fixture.yellowCards?.awayTeam?.length > 0 && (
//                         <div className="mb-2">
//                           <p className="text-xs text-gray-700 font-medium">Yellow</p>
//                           <ul className="space-y-1">
//                             {fixture.yellowCards.awayTeam.map((c, idx) => (
//                               <li key={idx} className="text-xs text-gray-700"><span className="font-medium">{c.playerName}</span> <span className="text-gray-600 ml-1">({c.cardsCount})</span></li>
//                             ))}
//                           </ul>
//                         </div>
//                       )}
//                       {fixture.redCards?.awayTeam?.length > 0 && (
//                         <div>
//                           <p className="text-xs text-gray-700 font-medium">Red</p>
//                           <ul className="space-y-1">
//                             {fixture.redCards.awayTeam.map((c, idx) => (
//                               <li key={idx} className="text-xs text-gray-700"><span className="font-medium">{c.playerName}</span> <span className="text-gray-600 ml-1">({c.cardsCount})</span></li>
//                             ))}
//                           </ul>
//                         </div>
//                       )}
//                     </div>
//                   </div>
//                 </div>
//               )}
//             </div>
//           ))}
//         </div>
//       )}
//     </div>
//   )
// }
